import React, { useRef, useState } from "react";
import {
    Box,
    Flex,
    FormControl,
    Input,
    Textarea,
    Text,
    useColorModeValue,
    useToast,
    Stack,
    Button,
} from "@chakra-ui/react";
import emailjs from "emailjs-com";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

const MotionBox = motion(Box);

// ─── Champ flottant ───────────────────────────────────────────────────────────
const FloatingField: React.FC<{
    label: string;
    name: string;
    type?: string;
    placeholder?: string;
    isTextarea?: boolean;
    isRequired?: boolean;
    index: number;
    isVisible: boolean;
}> = ({ label, name, type = "text", placeholder, isTextarea = false, isRequired = false, index, isVisible }) => {
    const [focused, setFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);

    const bg           = useColorModeValue("rgba(10,22,40,0.03)", "rgba(255,255,255,0.04)");
    const bgFocus      = useColorModeValue("rgba(10,22,40,0.05)", "rgba(255,255,255,0.07)");
    const borderColor  = useColorModeValue("rgba(10,22,40,0.10)", "rgba(255,255,255,0.10)");
    const borderFocus  = "#C9A84C";
    const textColor    = useColorModeValue("#0A1628", "#E8E0D0");
    const labelColor   = useColorModeValue("rgba(10,22,40,0.4)", "rgba(232,224,208,0.4)");
    const labelFocus   = "#C9A84C";

    const lifted = focused || hasValue;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.1 + index * 0.07, ease: [0.16, 1, 0.3, 1] }}
        >
            <FormControl isRequired={isRequired} position="relative">
                {/* Label flottant */}
                <motion.label
                    animate={{
                        y:        lifted ? 0  : (isTextarea ? 14 : 14),
                        scale:    lifted ? 1  : 1,
                        opacity:  lifted ? 1  : 0.55,
                        fontSize: lifted ? "10px" : "13px",
                    }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                        position:      "absolute",
                        top:           lifted ? "8px" : "0",
                        left:          "16px",
                        color:         focused ? labelFocus : labelColor,
                        pointerEvents: "none",
                        zIndex:        2,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        fontWeight:    "500",
                        lineHeight:    "1",
                        transformOrigin: "left",
                    }}
                >
                    {label}
                </motion.label>

                {isTextarea ? (
                    <Textarea
                        name={name}
                        placeholder=""
                        rows={5}
                        resize="none"
                        pt={7}
                        pb={3}
                        px={4}
                        bg={focused ? bgFocus : bg}
                        border="1px solid"
                        borderColor={focused ? borderFocus : borderColor}
                        borderRadius="16px"
                        color={textColor}
                        fontSize="sm"
                        fontWeight="300"
                        letterSpacing="0.01em"
                        transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
                        _focus={{ outline: "none", boxShadow: `0 0 0 1px ${borderFocus}20` }}
                        _placeholder={{ color: "transparent" }}
                        onFocus={() => setFocused(true)}
                        onBlur={(e) => { setFocused(false); setHasValue(e.target.value.length > 0); }}
                        onChange={(e) => setHasValue(e.target.value.length > 0)}
                    />
                ) : (
                    <Input
                        name={name}
                        type={type}
                        placeholder=""
                        pt={7}
                        pb={3}
                        px={4}
                        h="58px"
                        bg={focused ? bgFocus : bg}
                        border="1px solid"
                        borderColor={focused ? borderFocus : borderColor}
                        borderRadius="16px"
                        color={textColor}
                        fontSize="sm"
                        fontWeight="300"
                        letterSpacing="0.01em"
                        transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
                        _focus={{ outline: "none", boxShadow: `0 0 0 1px ${borderFocus}20` }}
                        _placeholder={{ color: "transparent" }}
                        onFocus={() => setFocused(true)}
                        onBlur={(e) => { setFocused(false); setHasValue(e.target.value.length > 0); }}
                        onChange={(e) => setHasValue(e.target.value.length > 0)}
                    />
                )}

                {/* Ligne accent au focus */}
                <motion.div
                    animate={{ scaleX: focused ? 1 : 0, opacity: focused ? 1 : 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                        position:        "absolute",
                        bottom:          0,
                        left:            "10%",
                        right:           "10%",
                        height:          "1px",
                        background:      "linear-gradient(90deg, transparent, #C9A84C, transparent)",
                        transformOrigin: "center",
                        borderRadius:    "0 0 16px 16px",
                    }}
                />
            </FormControl>
        </motion.div>
    );
};

// ─── Formulaire principal ─────────────────────────────────────────────────────
const ContactForm: React.FC = () => {
    const { t } = useTranslation();
    const form = useRef<HTMLFormElement>(null);
    const toast = useToast();
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const bg        = useColorModeValue("#FAFAF7", "#0A1628");
    const textColor = useColorModeValue("#0A1628", "#E8E0D0");
    const borderCol = useColorModeValue("rgba(10,22,40,0.08)", "rgba(255,255,255,0.06)");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.current) return;
        setSending(true);

        emailjs
            .sendForm("service_wv1mudh", "template_c9r2czc", form.current, "Nxaz4aDc87K5C-q1_")
            .then(() => {
                setSent(true);
                setSending(false);
                form.current?.reset();
                setTimeout(() => setSent(false), 4000);
            }, () => {
                setSending(false);
                toast({
                    title: t("formmesgF"),
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            });
    };

    const fields = [
        { label: t("formNom"),     name: "name",    type: "text",  index: 0 },
        { label: t("formPrenom"),  name: "prenom",  type: "text",  index: 1 },
        { label: t("formAdresse"), name: "email",   type: "email", index: 2 },
        { label: t("formTel"),     name: "tel",     type: "tel",   index: 3 },
    ];

    return (
        <Box
            bg={bg}
            borderRadius="28px"
            overflow="hidden"
            position="relative"
        >
            {/* Accent line top */}
            <Box
                h="1px"
                bg="linear-gradient(90deg, transparent, #C9A84C, #8B9E6E, transparent)"
                opacity={0.7}
            />

            <Box p={{ base: 8, md: 12 }}>

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Text
                        fontSize="9px"
                        letterSpacing="0.4em"
                        textTransform="uppercase"
                        color={textColor}
                        opacity={0.35}
                        fontWeight="500"
                        mb={3}
                        textAlign="center"
                    >
                        {t("formTitre")}
                    </Text>

                    <Text
                        fontSize={{ base: "2xl", md: "3xl" }}
                        fontWeight="200"
                        textAlign="center"
                        color={textColor}
                        letterSpacing="-0.02em"
                        lineHeight="1.1"
                        mb={2}
                    >
                        Parlons de votre{" "}
                        <Box as="span" fontWeight="700">projet</Box>
                    </Text>

                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        style={{ originX: 0.5, marginBottom: "2.5rem", marginTop: "1rem" }}
                    >
                        <Box w="40px" h="1px" bg="#C9A84C" opacity={0.6} mx="auto" />
                    </motion.div>
                </motion.div>

                {/* Formulaire */}
                <form ref={form} onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                        {/* Nom + Prénom côte à côte */}
                        <Flex direction={{ base: "column", md: "row" }} gap={4}>
                            <Box flex="1">
                                <FloatingField
                                    label={t("formNom")}
                                    name="name"
                                    isRequired
                                    index={0}
                                    isVisible
                                />
                            </Box>
                            <Box flex="1">
                                <FloatingField
                                    label={t("formPrenom")}
                                    name="prenom"
                                    isRequired
                                    index={1}
                                    isVisible
                                />
                            </Box>
                        </Flex>

                        <FloatingField
                            label={t("formAdresse")}
                            name="email"
                            type="email"
                            isRequired
                            index={2}
                            isVisible
                        />

                        <FloatingField
                            label={t("formTel")}
                            name="tel"
                            type="tel"
                            isRequired
                            index={3}
                            isVisible
                        />

                        <FloatingField
                            label={t("formBut")}
                            name="message"
                            isTextarea
                            isRequired
                            index={4}
                            isVisible
                        />

                        {/* Bouton */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                            style={{ display: "flex", justifyContent: "center", paddingTop: "0.5rem" }}
                        >
                            <AnimatePresence mode="wait">
                                {sent ? (
                                    <motion.div
                                        key="sent"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                    >
                                        <Flex
                                            align="center"
                                            gap={2}
                                            px={8}
                                            py={3}
                                            borderRadius="full"
                                            bg="rgba(139,158,110,0.12)"
                                            border="1px solid"
                                            borderColor="rgba(139,158,110,0.3)"
                                        >
                                            <Box w="6px" h="6px" borderRadius="full" bg="#8B9E6E" />
                                            <Text
                                                fontSize="xs"
                                                letterSpacing="0.2em"
                                                textTransform="uppercase"
                                                color="#8B9E6E"
                                                fontWeight="500"
                                            >
                                                {t("formmesgV")}
                                            </Text>
                                        </Flex>
                                    </motion.div>
                                ) : (
                                    <motion.div key="button">
                                        <Button
                                            type="submit"
                                            isLoading={sending}
                                            loadingText=""
                                            px={12}
                                            py={6}
                                            h="auto"
                                            bg={textColor}
                                            color={bg}
                                            fontWeight="400"
                                            fontSize="xs"
                                            letterSpacing="0.25em"
                                            textTransform="uppercase"
                                            borderRadius="full"
                                            border="1px solid transparent"
                                            transition="all 0.5s cubic-bezier(0.16, 1, 0.3, 1)"
                                            _hover={{
                                                bg: "transparent",
                                                color: textColor,
                                                borderColor: textColor,
                                                transform: "translateY(-2px)",
                                            }}
                                            _active={{
                                                transform: "translateY(0px)",
                                                opacity: 0.8,
                                            }}
                                            _loading={{
                                                opacity: 0.6,
                                            }}
                                        >
                                            {t("formEnvoyer")}
                                        </Button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </Stack>
                </form>
            </Box>
        </Box>
    );
};

export default ContactForm;